"use client";

import Link from "next/link";
import {
  Zap,
  AlertTriangle,
  ArrowRight,
  GitCompare,
  Lightbulb,
  ExternalLink,
} from "lucide-react";
import type { CompareFrontmatter } from "@/lib/content";
import {
  DiagnosticFlowchart,
  type FlowStep,
} from "@/components/compare/DiagnosticFlowchart";

interface Props {
  frontmatter: CompareFrontmatter;
  children?: React.ReactNode; // MDX content (추가 설명)
}

export function ComparePageClient({ frontmatter, children }: Props) {
  const { diseaseA, diseaseB } = frontmatter;

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
          <Link href="/compare" className="hover:text-foreground">
            감별진단 비교
          </Link>
          <span>/</span>
          <span>{frontmatter.title}</span>
        </div>
        <h1 className="text-2xl font-bold flex items-center gap-3">
          <GitCompare className="h-6 w-6 text-clinical-purple" />
          {diseaseA.name}
          <span className="text-muted-foreground font-normal text-lg">vs</span>
          {diseaseB.name}
        </h1>
      </div>

      {/* 한 줄 요약 */}
      <div className="rounded-xl border-2 border-clinical-purple/30 bg-clinical-purple/5 p-5">
        <div className="flex items-start gap-3">
          <Zap className="h-5 w-5 text-clinical-purple mt-0.5 shrink-0" />
          <p className="text-base font-semibold leading-relaxed">
            {frontmatter.oneLiner}
          </p>
        </div>
      </div>

      {/* 5-second differentiation */}
      <section>
        <h2 className="text-lg font-bold mb-3 flex items-center gap-2">
          <span className="inline-flex items-center justify-center h-7 w-7 rounded-lg bg-amber-500/15 text-amber-600 text-xs font-bold">
            5s
          </span>
          5-Second Differentiation
        </h2>
        <div className="rounded-xl border overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/60">
                <th className="px-4 py-2.5 text-left font-semibold text-muted-foreground w-[30%]">
                  감별 포인트
                </th>
                <th className="px-4 py-2.5 text-left font-semibold text-clinical-blue w-[35%]">
                  {diseaseA.name}
                </th>
                <th className="px-4 py-2.5 text-left font-semibold text-clinical-red w-[35%]">
                  {diseaseB.name}
                </th>
              </tr>
            </thead>
            <tbody>
              {frontmatter.fiveSecond.map((item, i) => (
                <tr key={i} className="border-t">
                  <td className="px-4 py-2.5 font-medium">{item.point}</td>
                  <td className="px-4 py-2.5 text-clinical-blue">{item.a}</td>
                  <td className="px-4 py-2.5 text-clinical-red">{item.b}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* 언제 A를 / 언제 B를 더 의심? */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-xl border-2 border-clinical-blue/20 bg-clinical-blue/5 p-5">
          <h3 className="font-bold text-clinical-blue mb-2 flex items-center gap-2">
            <ArrowRight className="h-4 w-4" />
            언제 {diseaseA.name}을(를) 더 의심?
          </h3>
          <p className="text-sm leading-relaxed whitespace-pre-line">
            {frontmatter.suspectA}
          </p>
        </div>
        <div className="rounded-xl border-2 border-clinical-red/20 bg-clinical-red/5 p-5">
          <h3 className="font-bold text-clinical-red mb-2 flex items-center gap-2">
            <ArrowRight className="h-4 w-4" />
            언제 {diseaseB.name}을(를) 더 의심?
          </h3>
          <p className="text-sm leading-relaxed whitespace-pre-line">
            {frontmatter.suspectB}
          </p>
        </div>
      </section>

      {/* Side-by-side 비교표 */}
      <section>
        <h2 className="text-lg font-bold mb-3">Side-by-Side 비교</h2>
        <div className="rounded-xl border overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/60">
                <th className="px-4 py-2.5 text-left font-semibold text-muted-foreground w-[20%]">
                  항목
                </th>
                <th className="px-4 py-2.5 text-left font-semibold text-clinical-blue w-[40%]">
                  {diseaseA.name}
                </th>
                <th className="px-4 py-2.5 text-left font-semibold text-clinical-red w-[40%]">
                  {diseaseB.name}
                </th>
              </tr>
            </thead>
            <tbody>
              {frontmatter.comparison.map((row, i) => (
                <tr
                  key={i}
                  className={`border-t ${i % 2 === 1 ? "bg-muted/20" : ""}`}
                >
                  <td className="px-4 py-2.5 font-medium text-muted-foreground">
                    {row.label}
                  </td>
                  <td className="px-4 py-2.5">{row.a}</td>
                  <td className="px-4 py-2.5">{row.b}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* 함정 포인트 */}
      {frontmatter.pitfalls && frontmatter.pitfalls.length > 0 && (
        <section className="rounded-xl border-2 border-amber-500/30 bg-amber-500/5 p-5">
          <h2 className="text-lg font-bold mb-3 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            함정 포인트 / 흔한 오진
          </h2>
          <ul className="space-y-2">
            {frontmatter.pitfalls.map((p, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                <span className="text-amber-600 mt-0.5 shrink-0">!</span>
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* 이미지 비교 */}
      {frontmatter.imageCompare && frontmatter.imageCompare.length > 0 && (
        <section>
          <h2 className="text-lg font-bold mb-3">이미지 비교</h2>
          <div className="space-y-4">
            {frontmatter.imageCompare.map((img, i) => (
              <div key={i}>
                <div className="text-xs font-semibold text-muted-foreground uppercase mb-2">
                  {img.type}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-lg border overflow-hidden">
                    {img.srcA ? (
                      <>
                        <div className="aspect-[4/3] bg-black">
                          <img
                            src={img.srcA}
                            alt={img.altA || diseaseA.name}
                            className="h-full w-full object-contain"
                          />
                        </div>
                        <div className="px-3 py-2 bg-clinical-blue/5 text-xs">
                          <span className="font-semibold text-clinical-blue">
                            {diseaseA.name}
                          </span>
                          {img.captionA && (
                            <span className="text-muted-foreground">
                              {" "}— {img.captionA}
                            </span>
                          )}
                        </div>
                      </>
                    ) : (
                      <div className="aspect-[4/3] bg-muted/30 flex items-center justify-center text-xs text-muted-foreground">
                        이미지 없음
                      </div>
                    )}
                  </div>
                  <div className="rounded-lg border overflow-hidden">
                    {img.srcB ? (
                      <>
                        <div className="aspect-[4/3] bg-black">
                          <img
                            src={img.srcB}
                            alt={img.altB || diseaseB.name}
                            className="h-full w-full object-contain"
                          />
                        </div>
                        <div className="px-3 py-2 bg-clinical-red/5 text-xs">
                          <span className="font-semibold text-clinical-red">
                            {diseaseB.name}
                          </span>
                          {img.captionB && (
                            <span className="text-muted-foreground">
                              {" "}— {img.captionB}
                            </span>
                          )}
                        </div>
                      </>
                    ) : (
                      <div className="aspect-[4/3] bg-muted/30 flex items-center justify-center text-xs text-muted-foreground">
                        이미지 없음
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* 진단 알고리즘 (Flowchart) */}
      {(frontmatter as CompareFrontmatter & { algorithmSteps?: FlowStep }).algorithmSteps && (
        <section>
          <h2 className="text-lg font-bold mb-3 flex items-center gap-2">
            <Lightbulb className="h-5 w-5 text-clinical-green" />
            진단 알고리즘
          </h2>
          <DiagnosticFlowchart
            steps={(frontmatter as CompareFrontmatter & { algorithmSteps: FlowStep }).algorithmSteps}
          />
        </section>
      )}

      {/* 텍스트 알고리즘 (fallback) */}
      {frontmatter.algorithm && !(frontmatter as CompareFrontmatter & { algorithmSteps?: FlowStep }).algorithmSteps && (
        <section>
          <h2 className="text-lg font-bold mb-3 flex items-center gap-2">
            <Lightbulb className="h-5 w-5 text-clinical-green" />
            진단 알고리즘
          </h2>
          <div className="rounded-xl border bg-card p-5">
            <pre className="text-sm whitespace-pre-wrap leading-relaxed font-mono">
              {frontmatter.algorithm}
            </pre>
          </div>
        </section>
      )}

      {/* MDX 추가 콘텐츠 */}
      {children && (
        <section className="prose prose-sm dark:prose-invert max-w-none">
          {children}
        </section>
      )}

      {/* 관련 질환 더 보기 */}
      <section className="rounded-xl border bg-card p-5">
        <h2 className="text-base font-bold mb-3">관련 질환 더 보기</h2>
        <div className="flex flex-wrap gap-2">
          <Link
            href={`/${diseaseA.subspecialty}/${diseaseA.slug}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-clinical-blue/10 text-clinical-blue text-sm font-medium hover:bg-clinical-blue/20 transition-colors"
          >
            {diseaseA.name}
            <ExternalLink className="h-3 w-3" />
          </Link>
          <Link
            href={`/${diseaseB.subspecialty}/${diseaseB.slug}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-clinical-red/10 text-clinical-red text-sm font-medium hover:bg-clinical-red/20 transition-colors"
          >
            {diseaseB.name}
            <ExternalLink className="h-3 w-3" />
          </Link>
          {frontmatter.related &&
            frontmatter.related.map((r, i) => (
              <Link
                key={i}
                href={`/compare/${r}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-clinical-purple/10 text-clinical-purple text-sm font-medium hover:bg-clinical-purple/20 transition-colors"
              >
                관련 비교: {r}
                <GitCompare className="h-3 w-3" />
              </Link>
            ))}
        </div>
      </section>
    </div>
  );
}
